import React from "react";

import { Box, Typography } from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";

import Waveform from "../Waveform";
import BackgroundLayer from "./BackgroundLayer";
import { AudiogramEditorState } from "../../types";

interface BoldBrandProps {
  state: AudiogramEditorState;
  scale?: number;
  audioLevels?: number[];
  isPlaying?: boolean;
}

export default function BoldBrand({
  state,
  scale = 1,
  audioLevels,
  isPlaying,
}: BoldBrandProps) {
  const {
    quote,
    fontFamily,
    fontSize,
    textColor,
    showName,
    guestName,
    accentColor,
    waveformStyle,
    waveformColor,
    waveformHeight,
    animated,
    backgroundImage,
    backgroundOverlay,
    backgroundFit,
  } = state;

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        backgroundColor: "#0A1628",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        p: 3 * scale,
        boxSizing: "border-box",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {backgroundImage && (
        <BackgroundLayer
          image={backgroundImage}
          overlay={backgroundOverlay}
          overlayColor="#0A1628"
          fit={backgroundFit}
        />
      )}

      {/* Content layer — above background */}
      <Box
        sx={{
          position: "relative",
          zIndex: 3,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          flex: 1,
        }}
      >
        {/* Top row: show name + badge */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              color: accentColor,
              fontSize: 10 * scale,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.1em",
              lineHeight: 1,
            }}
          >
            {showName}
          </Typography>
          <Box
            sx={{
              backgroundColor: `${accentColor}22`,
              border: `1px solid ${accentColor}44`,
              borderRadius: 10 * scale,
              px: 1.2 * scale,
              py: 0.3 * scale,
            }}
          >
            <Typography
              sx={{
                color: accentColor,
                fontSize: 8 * scale,
                fontWeight: 600,
                letterSpacing: "0.03em",
              }}
            >
              New episode
            </Typography>
          </Box>
        </Box>

        {/* Guest photo placeholder */}
        {!backgroundImage && (
          <Box
            sx={{
              width: "100%",
              aspectRatio: "4/3",
              backgroundColor: "#0D1F3C",
              borderRadius: 2 * scale,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mt: 2 * scale,
              flexShrink: 0,
              maxHeight: "30%",
            }}
          >
            <MicIcon
              sx={{
                fontSize: 36 * scale,
                color: "rgba(255,255,255,0.12)",
              }}
            />
          </Box>
        )}

        {/* Quote */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            py: 2 * scale,
            px: 0.5 * scale,
          }}
        >
          <Typography
            sx={{
              color: textColor,
              fontSize: fontSize * scale,
              fontWeight: 500,
              fontFamily: fontFamily || "'Inter', sans-serif",
              lineHeight: 1.6,
              letterSpacing: "-0.005em",
              display: "-webkit-box",
              WebkitLineClamp: 10,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {quote}
          </Typography>
        </Box>

        {/* Waveform */}
        <Box>
          <Waveform
            style={waveformStyle}
            color={waveformColor || accentColor}
            height={waveformHeight}
            animated={animated}
            barCount={56}
            levels={audioLevels}
            playing={isPlaying}
          />
          {/* Progress bar */}
          <Box
            sx={{
              mt: 0.8 * scale,
              height: 2 * scale,
              backgroundColor: "rgba(255,255,255,0.08)",
              borderRadius: "1px",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                width: "35%",
                height: "100%",
                backgroundColor: accentColor,
                borderRadius: "1px",
              }}
            />
          </Box>
        </Box>

        {/* Divider */}
        <Box
          sx={{
            height: "1px",
            backgroundColor: "rgba(255,255,255,0.08)",
            mt: 2 * scale,
            mb: 1.5 * scale,
          }}
        />

        {/* Guest name */}
        <Typography
          sx={{
            color: "#FFFFFF",
            fontSize: 14 * scale,
            fontWeight: 700,
            lineHeight: 1.2,
          }}
        >
          {guestName}
        </Typography>
      </Box>
    </Box>
  );
}
